import React from 'react';

type Props = React.PropsWithChildren<{
  /** Heading for the fallback; defaults to a generic message. */
  title?: string;
  /** When given, the fallback offers a way out (e.g. close the overlay). */
  onClose?: () => void;
  closeLabel?: string;
}>;

type State = { hasError: boolean; error?: Error | null; stack?: string | null };

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null, stack: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, stack: error?.stack ?? null };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
    this.setState({ stack: info?.componentStack || error?.stack || null });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const { title, onClose, closeLabel } = this.props;
    return (
      <div className="flex-1 min-h-0 overflow-auto p-4 bg-white dark:bg-slate-950">
        <div className="mx-auto max-w-lg rounded-xl border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-200 p-4">
          <div className="font-semibold mb-1">{title || 'Es ist ein Fehler aufgetreten'}</div>
          <div className="text-sm whitespace-pre-wrap break-words">
            {this.state.error?.message || 'Unbekannter Fehler'}
          </div>
          {/* Shown inline because this can happen on a phone, where there is no
              console to open — screenshot it and the stack is enough to debug. */}
          {this.state.stack && (
            <pre className="mt-3 max-h-64 overflow-auto rounded-lg bg-white/60 dark:bg-black/30 p-2 text-[10px] leading-snug whitespace-pre-wrap break-words">
              {this.state.stack.trim()}
            </pre>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full rounded-lg bg-red-600 text-white text-sm font-medium px-4 py-2 active:scale-95 transition"
            >
              {closeLabel || 'Schließen'}
            </button>
          )}
        </div>
      </div>
    );
  }
}
